// src/components/DynamicMenu/DynamicMenu.js
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useDynamicModules from "../../hooks/useDynamicModules";
import hub from "../../services/EventHub";
import menuIconMap from "../menuIconMap";
import menuLabelMap from "../menuLabelMap";

import { Tour } from "antd";

import {
  FaTachometerAlt,
  FaConciergeBell,
  FaBed,
  FaBroom,
  FaTools,
  FaCogs,
  FaRegFileAlt,
} from "react-icons/fa";

import "./DynamicMenu.css";

/* ---- 1) Group definitions (icon + label) ------------------- */

const GROUP_DEFS = {
  dashboard: { key: "dashboard", label: "Dashboard", icon: FaTachometerAlt },
  frontOffice: { key: "frontOffice", label: "Front Office", icon: FaConciergeBell },
  rooms: { key: "rooms", label: "Rooms", icon: FaBed },
  housekeeping: { key: "housekeeping", label: "Housekeeping", icon: FaBroom },
  maintenance: { key: "maintenance", label: "Maintenance", icon: FaTools },
  admin: { key: "admin", label: "Administration", icon: FaCogs },
  other: { key: "other", label: "Other", icon: FaRegFileAlt },
};

/**
 * Map each backend component name to a group key.
 * NOTE: mapping is case-sensitive; match backend `component` exactly.
 */
const COMPONENT_GROUP_MAP = {
  // Dashboard
  Dashboard: "dashboard",
  AdminDashboard: "dashboard",

  // Front Office
  BookingPage: "frontOffice",
  BookingListPage: "frontOffice",
  CheckInOutPage: "frontOffice",
  CheckInPage: "frontOffice",
  CheckOutPage: "frontOffice",
  CheckIn: "frontOffice",
  CheckOut: "frontOffice",
  ReservationCalendarPage: "frontOffice",
  ReservationGantt: "frontOffice",
  GuestListPage: "frontOffice",
  GuestProfilesPage: "frontOffice",
  RestaurantOrderPage: "frontOffice",
  CreateServicePage: "frontOffice",

  // Rooms
  RoomTypesPage: "rooms",
  RoomPricePage: "rooms",
  RoomListPage: "rooms",
  RoomStatusPage: "rooms",
  RoomImagesPage: "rooms",
  BedTypesPage: "rooms",
  FloorPlansPage: "rooms",

  // Housekeeping
  HousekeeperDashboard: "housekeeping",
  HousekeepingPage: "housekeeping",
  HousekeepingReportsPage: "housekeeping",

  // Maintenance
  MaintenanceDashboard: "maintenance",
  MaintenancePage: "maintenance",
  MaintenanceRequestForm: "maintenance",

  // Admin / Platform
  ManageUsers: "admin",
  AddUserPage: "admin",
  ManageStaffPage: "admin",
  ModuleAssignment: "admin",
  PermissionManager: "admin",
  SettingsPage: "admin",
  HotelSettings: "admin",
  SetupHotelPage: "admin",
  KycReviewPage: "admin",
  KYCVerificationPage: "admin",
  KYCUploadPage: "admin",
  SystemLogPage: "admin",
  SupportAdminPage: "admin",
  AdminLegalDocsPage: "admin",
};

/**
 * Ordering inside groups (lower = earlier).
 * Rooms order = Room Type, Room Price, Room List, Room Status.
 * Front Office: Reservation first (per request).
 */
const GROUP_ITEM_ORDER = {
  // Front Office (Reservation first)
  ReservationCalendarPage: 1,
  ReservationGantt: 2,

  BookingPage: 3,
  BookingListPage: 4,

  CheckInOutPage: 5,
  CheckIn: 5,
  CheckInPage: 5,

  CheckOut: 6,
  CheckOutPage: 6,

  GuestListPage: 10,
  GuestProfilesPage: 11,
  RestaurantOrderPage: 20,
  CreateServicePage: 30,

  // Rooms (requested order)
  RoomTypesPage: 1,
  RoomPricePage: 2,
  RoomListPage: 3,
  RoomStatusPage: 4,

  // Remaining Rooms
  RoomImagesPage: 10,
  BedTypesPage: 20,
  FloorPlansPage: 30,
};

/**
 * Groups that should show right-aligned numeric order badges (not part of label text).
 */
const ORDER_BADGE_GROUPS = new Set(["rooms"]);

/**
 * Rooms setup tour (first login) version.
 * Bump the version to re-run tour for everyone if needed later.
 */
const ROOMS_TOUR_VERSION = "v1";

/* ---- helpers for route matching --------------------------- */

const normalize = (p) => (p === "/" ? "/" : (p || "").replace(/\/+$/, ""));

const matches = (pathname, itemPath) =>
  itemPath === "/"
    ? pathname === "/"
    : pathname === itemPath || pathname.startsWith(itemPath + "/");

/* ================== DynamicMenu ============================ */

const DynamicMenu = ({ showLabels }) => {
  const queryClient = useQueryClient();
  const { authData } = useAuth();
  const { data: modules = [], isLoading, error } = useDynamicModules();
  const location = useLocation();
  const navigate = useNavigate();

  /* 1. Permissions refresh from EventHub */
  useEffect(() => {
    const unsubscribe = hub.subscribe("permissionsChanged", () => {
      queryClient.invalidateQueries({
        queryKey: ["modules", authData?.roleId, authData?.hotelId],
      });
    });
    return unsubscribe;
  }, [queryClient, authData?.roleId, authData?.hotelId]);

  /* 2. Flatten modules -> menu items (with groupKey) */
  const items = useMemo(() => {
    if (!modules.length) return [];

    return modules.map((m) => {
      const component = m.component;
      const groupKey = COMPONENT_GROUP_MAP[component] || "other";
      const IconComp = menuIconMap[m.icon ?? component] ?? null;

      const rawPath = m.path ?? "";
      const to = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

      return {
        key: m.moduleId ?? m.path ?? component,
        to,
        label: menuLabelMap[component] ?? m.friendlyName ?? component,
        icon: IconComp,
        component,
        groupKey,
      };
    });
  }, [modules]);

  /**
   * Build a quick lookup: component -> route path
   * Used by the Tour to navigate users to the correct setup pages.
   */
  const pathByComponent = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (it?.component && it?.to) map.set(it.component, it.to);
    }
    return map;
  }, [items]);

  /* 3. Group the items by groupKey + stable ordering + attach order badges */
  const groups = useMemo(() => {
    const map = new Map();

    for (const item of items) {
      const def = GROUP_DEFS[item.groupKey] ?? GROUP_DEFS.other;
      const gKey = def.key;

      if (!map.has(gKey)) {
        map.set(gKey, {
          key: gKey,
          label: def.label,
          Icon: def.icon,
          items: [],
        });
      }
      map.get(gKey).items.push(item);
    }

    // Sort items within each group (predictable UX)
    for (const g of map.values()) {
      g.items.sort((a, b) => {
        const wa = GROUP_ITEM_ORDER[a.component] ?? 999;
        const wb = GROUP_ITEM_ORDER[b.component] ?? 999;
        if (wa !== wb) return wa - wb;
        return (a.label || "").localeCompare(b.label || "");
      });

      // Add order badges (rooms group only)
      if (ORDER_BADGE_GROUPS.has(g.key)) {
        g.items = g.items.map((it, idx) => ({ ...it, orderNo: idx + 1 }));
      } else {
        g.items = g.items.map((it) => ({ ...it, orderNo: null }));
      }
    }

    // Keep explicit group ordering from GROUP_DEFS
    const ordered = [];
    Object.keys(GROUP_DEFS).forEach((k) => {
      const g = map.get(k);
      if (g && g.items.length) ordered.push(g);
    });

    // Any unexpected extra groups
    for (const g of map.values()) {
      if (!ordered.find((x) => x.key === g.key)) ordered.push(g);
    }

    return ordered;
  }, [items]);

  /* 4. Find active group + item based on current route */
  const active = useMemo(() => {
    const pathname = normalize(location.pathname);
    let best = null;
    let bestLen = -1;

    groups.forEach((g) => {
      g.items.forEach((it) => {
        const p = normalize(it.to);
        if (matches(pathname, p) && p.length > bestLen) {
          bestLen = p.length;
          best = { groupKey: g.key, itemKey: it.key };
        }
      });
    });

    return best;
  }, [groups, location.pathname]);

  /* 5. Only one group open at a time */
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    if (!groups.length) return;

    setOpenGroup((prev) => {
      if (!showLabels) return prev;

      if (!prev || !groups.some((g) => g.key === prev)) {
        if (active?.groupKey && groups.some((g) => g.key === active.groupKey)) {
          return active.groupKey;
        }
        return groups[0].key;
      }

      if (active?.groupKey && active.groupKey !== prev) {
        return active.groupKey;
      }

      return prev;
    });
  }, [groups, active?.groupKey, showLabels]);

  const handleGroupClick = (group) => {
    const first = group.items?.[0];

    if (showLabels) {
      setOpenGroup(group.key);

      if (first?.to) {
        const current = normalize(location.pathname);
        const target = normalize(first.to);
        if (!matches(current, target)) {
          navigate(first.to);
        }
      }
      return;
    }

    if (first?.to) navigate(first.to);
  };

  /* 6. First-login Rooms Setup Tour */
  const [roomsTourOpen, setRoomsTourOpen] = useState(false);
  const [roomsTourStep, setRoomsTourStep] = useState(0);

  const roomsTourStorageKey = useMemo(() => {
    const uid = authData?.userId ?? "anon";
    const hid = authData?.hotelId ?? "0";
    return `hms:onboarding:roomsSetup:${ROOMS_TOUR_VERSION}:${hid}:${uid}`;
  }, [authData?.userId, authData?.hotelId]);

  const hasRoomsSetupModules = useMemo(() => {
    return (
      pathByComponent.has("RoomTypesPage") ||
      pathByComponent.has("RoomPricePage") ||
      pathByComponent.has("RoomListPage")
    );
  }, [pathByComponent]);

  useEffect(() => {
    if (!showLabels) return;
    if (!items.length) return;
    if (!hasRoomsSetupModules) return;

    const seen = localStorage.getItem(roomsTourStorageKey);
    if (seen) return;

    setOpenGroup("rooms");
    setRoomsTourStep(0);
    setRoomsTourOpen(true);
  }, [showLabels, items.length, roomsTourStorageKey, hasRoomsSetupModules]);

  const closeRoomsTour = () => {
    localStorage.setItem(roomsTourStorageKey, "1");
    setRoomsTourOpen(false);
  };

  const goToComponent = (componentName) => {
    const to = pathByComponent.get(componentName);
    if (to) navigate(to);
  };

  const handleRoomsTourChange = (nextStep) => {
    setRoomsTourStep(nextStep);
    setOpenGroup("rooms");

    if (nextStep === 2) goToComponent("RoomTypesPage");
    if (nextStep === 4) goToComponent("RoomPricePage");
    if (nextStep === 6) goToComponent("RoomListPage");
  };

  const roomsTourSteps = useMemo(() => {
    return [
      {
        title: "Rooms Setup (First Time)",
        description:
          "To start using Rooms, follow this order: 1) Room Types, 2) Room Prices, 3) Room List (create rooms).",
        target: () => document.getElementById("tour-group-rooms"),
        placement: "right",
      },
      {
        title: "Step 1: Room Types",
        description: "Define your room types first (e.g., Standard, Deluxe, Suite).",
        target: () => document.getElementById("tour-menu-RoomTypesPage"),
        placement: "right",
      },
      {
        title: "Create a Room Type",
        description: "Click the Add/New button to create your first room type.",
        target: () => document.getElementById("tour-roomtypes-add"),
        placement: "bottom",
      },
      {
        title: "Step 2: Room Prices",
        description: "Set prices for your room types (per night, seasonal rates, etc.).",
        target: () => document.getElementById("tour-menu-RoomPricePage"),
        placement: "right",
      },
      {
        title: "Create a Price",
        description: "Add a price/rate for at least one room type.",
        target: () => document.getElementById("tour-roomprice-add"),
        placement: "bottom",
      },
      {
        title: "Step 3: Room List",
        description: "Now create the actual rooms and assign their type (Room 101, Room 102...).",
        target: () => document.getElementById("tour-menu-RoomListPage"),
        placement: "right",
      },
      {
        title: "Create a Room",
        description: "Add your rooms here (number, type, floor, etc.).",
        target: () => document.getElementById("tour-roomlist-add"),
        placement: "bottom",
      },
    ];
  }, []);

  /* 7. Loading / error / empty states */
  if (isLoading) return <div className="menu-loading">Loading…</div>;
  if (error) return <div className="menu-error">Failed to load menu</div>;
  if (!items.length) return <div className="menu-empty">No items</div>;

  /* 8. UI: grouped nav (design unchanged) */
  return (
    <>
      <nav className={`dyn-menu ${showLabels ? "expanded" : "collapsed"}`}>
        {groups.map((group) => {
          const { key, label, Icon, items: groupItems } = group;
          const isOpen = showLabels && openGroup === key;
          const isGroupActive = active?.groupKey === key;

          return (
            <div
              key={key}
              className={`dyn-menu-section ${isOpen ? "open" : ""} ${
                isGroupActive ? "active" : ""
              }`}
            >
              <button
                id={key === "rooms" ? "tour-group-rooms" : undefined}
                type="button"
                className="dyn-menu-group"
                onClick={() => handleGroupClick(group)}
                title={label}
              >
                <span className="dyn-menu-icon">{Icon && <Icon />}</span>
                {showLabels && <span className="dyn-menu-group-label">{label}</span>}
              </button>

              {showLabels && isOpen && (
                <ul className="dyn-menu-items">
                  {groupItems.map((it) => (
                    <li key={it.key} className="dyn-menu-item">
                      <NavLink
                        id={
                          it.component === "RoomTypesPage"
                            ? "tour-menu-RoomTypesPage"
                            : it.component === "RoomPricePage"
                            ? "tour-menu-RoomPricePage"
                            : it.component === "RoomListPage"
                            ? "tour-menu-RoomListPage"
                            : undefined
                        }
                        to={it.to}
                        end
                        className={({ isActive }) =>
                          `dyn-menu-link ${isActive ? "active" : ""}`
                        }
                      >
                        <span className="dyn-menu-link-row">
                          <span className="dyn-menu-item-bullet" />
                          <span className="dyn-menu-item-label">{it.label}</span>

                          {it.orderNo ? (
                            <span
                              className="dyn-menu-item-order"
                              aria-label={`Order ${it.orderNo}`}
                              title={`Order ${it.orderNo}`}
                            >
                              {it.orderNo}
                            </span>
                          ) : null}
                        </span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      <Tour
        open={roomsTourOpen}
        current={roomsTourStep}
        onChange={handleRoomsTourChange}
        onClose={closeRoomsTour}
        steps={roomsTourSteps}
        mask
      />
    </>
  );
};

export default React.memo(DynamicMenu);
